import { FC, useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { PlatformCard } from './PlatformCard';
import { IPlatformAdmin } from './PlatformLayout';
import { errorMessage, formatDateTime, platformApi } from './platformApi';

const MIN_PASSWORD = 8;
const EMPTY = { name: '', email: '', password: '', role: 'staff' };

const PlatformTeam: FC = () => {
  const me = useOutletContext<IPlatformAdmin | undefined>();
  const isOwner = me?.role === 'owner';
  const [admins, setAdmins] = useState<IPlatformAdmin[]>([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    platformApi.get('/admins')
      .then((response) => { setAdmins(response.data.admins); setError(''); })
      .catch((err) => setError(errorMessage(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key: string) => (e: any) => setForm({ ...form, [key]: e.target.value });

  const create = async () => {
    setFormError('');
    setSaving(true);
    try {
      await platformApi.post('/admins', form);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err: any) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (admin: IPlatformAdmin, isActive: boolean) => {
    try {
      await platformApi.put(`/admins/${admin._id}/active`, { isActive });
      load();
    } catch (err: any) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      <PlatformCard
        title='Wareozo staff with control panel access'
        action={isOwner ? <Button size='small' variant='outlined' onClick={() => setOpen(true)} sx={{ textTransform: 'none', height: 28, bgcolor: '#fff' }}>Add staff</Button> : undefined}
      >
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Last sign-in</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin._id}>
                <TableCell>{admin.name}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.role === 'owner' ? 'Owner' : 'Staff'}</TableCell>
                <TableCell>{formatDateTime(admin.lastLoginAt)}</TableCell>
                <TableCell>{admin.isActive ? 'Active' : 'Deactivated'}</TableCell>
                <TableCell align='right'>
                  {isOwner && admin._id !== me?._id && (
                    <Button size='small' variant='outlined' color={admin.isActive ? 'error' : 'primary'} onClick={() => setActive(admin, !admin.isActive)} sx={{ textTransform: 'none', height: 28 }}>
                      {admin.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PlatformCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>Add staff</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {formError && <Alert severity='error' sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField label='Name' size='small' fullWidth required value={form.name} onChange={setField('name')} /></Grid>
            <Grid item xs={6}><TextField label='Email' type='email' size='small' fullWidth required value={form.email} onChange={setField('email')} /></Grid>
            <Grid item xs={6}><TextField label='Password' type='password' size='small' fullWidth required autoComplete='new-password' value={form.password} onChange={setField('password')} helperText={`At least ${MIN_PASSWORD} characters`} /></Grid>
            <Grid item xs={6}>
              <TextField select label='Role' size='small' fullWidth value={form.role} onChange={setField('role')} helperText='Owners can also manage this team'>
                <MenuItem value='staff'>Staff</MenuItem>
                <MenuItem value='owner'>Owner</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size='small' variant='outlined' onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <LoadingButton size='small' variant='contained' loading={saving} disabled={!form.name || !form.email || form.password.length < MIN_PASSWORD} onClick={create} sx={{ textTransform: 'none' }}>Add</LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlatformTeam;
